export const INSTANCE_ROLE_NONE = 'none'
export const INSTANCE_ROLE_CHIEF_OF_STAFF = 'chief_of_staff'

export type InstanceRole =
  | typeof INSTANCE_ROLE_NONE
  | typeof INSTANCE_ROLE_CHIEF_OF_STAFF

export function readInstanceRole(value: unknown): InstanceRole {
  const raw = String(value ?? '')
    .trim()
    .toLowerCase()
  if (raw === INSTANCE_ROLE_CHIEF_OF_STAFF) {
    return INSTANCE_ROLE_CHIEF_OF_STAFF
  }
  return INSTANCE_ROLE_NONE
}

export function isChiefOfStaffDeployment(item: {
  instanceRole?: string | null
  instance_role?: string | null
}): boolean {
  return (
    readInstanceRole(item.instanceRole ?? item.instance_role) ===
    INSTANCE_ROLE_CHIEF_OF_STAFF
  )
}

/** Chef d'orchestre node: Studio threads are instance-global, not Application-scoped. */
export function studioNodeImpliesGlobalScope(
  agentId: string | null | undefined,
  chiefOfStaffId: string | null | undefined,
): boolean {
  const host = agentId?.trim()
  const reserved = chiefOfStaffId?.trim()
  return Boolean(host && reserved && host === reserved)
}

export function isBusinessDeployment(item: {
  isAdmin?: boolean
  instanceRole?: string | null
  instance_role?: string | null
}): boolean {
  return item.isAdmin !== true && !isChiefOfStaffDeployment(item)
}

export function splitBusinessDeployments<T extends {
  isAdmin?: boolean
  instanceRole?: string | null
  instance_role?: string | null
}>(items: T[]): { deployments: T[]; chiefOfStaff: T | null } {
  let chiefOfStaff: T | null = null
  const deployments: T[] = []
  for (const item of items) {
    if (isChiefOfStaffDeployment(item)) {
      if (!chiefOfStaff) {
        chiefOfStaff = item
      }
      continue
    }
    deployments.push(item)
  }
  return { deployments, chiefOfStaff }
}

/** `/studio/chat` without `agentId` lands on the reserved instance host. */
export function resolvePreferredStudioHost(input: {
  pathname: string
  urlAgentId: string | null
  chiefOfStaffId: string | null
  liveDeploymentId: string | null
  storedDeploymentId: string | null
}): string | null {
  if (input.urlAgentId) {
    return input.urlAgentId
  }
  if (input.pathname.startsWith('/studio/chat') && input.chiefOfStaffId) {
    return input.chiefOfStaffId
  }
  return input.liveDeploymentId || input.storedDeploymentId
}

/** Chat / panel host: URL first, then Chef d’orchestre on `/studio/chat`, never a leftover Agent. */
export function resolveStudioSurfaceHostId(input: {
  overrideAgentId?: string | null
  pathname: string
  urlAgentId: string | null
  chiefOfStaffId: string | null
  storeAgentId: string | null
}): string | null {
  const override = input.overrideAgentId?.trim() || null
  if (override) return override
  return resolvePreferredStudioHost({
    pathname: input.pathname,
    urlAgentId: input.urlAgentId,
    chiefOfStaffId: input.chiefOfStaffId,
    liveDeploymentId: input.storeAgentId,
    storedDeploymentId: null,
  })
}

/** Keep a named Studio host (URL / Chef d’orchestre) even before it is in the catalog. */
export function resolveBootstrapHostId(input: {
  preferredId: string | null
  liveId: string | null
  fallbackId: string | null
}): string | null {
  const preferred = input.preferredId?.trim() || null
  if (preferred) return preferred
  if (input.liveId) return input.liveId
  return input.fallbackId
}

/**
 * Drop threadId only on a real Agent switch (A → B).
 * Org hydration and host catch-up (null → id) must keep the URL fil.
 */
export function shouldClearStudioThreadOnHostChange(input: {
  previousAgentId: string | null | undefined
  nextAgentId: string | null | undefined
}): boolean {
  const previous = input.previousAgentId?.trim() || ''
  const next = input.nextAgentId?.trim() || ''
  return Boolean(previous && next && previous !== next)
}

/** First Agent created/bound by an applied architecture plan, other than the current host. */
export function studioHandoffAgentId(
  plan: {
    blast_radius?: { agents?: string[] }
    operations?: Array<{ op?: string; agent_id?: string }>
    result?: Record<string, unknown>
  },
  currentAgentId?: string | null,
): string | null {
  const current = currentAgentId?.trim() || ''
  const followUps = plan.result?.follow_up_agent_spec_plans
  if (Array.isArray(followUps)) {
    for (const item of followUps) {
      if (!item || typeof item !== 'object') continue
      const id = String(
        (item as { agent_id?: unknown }).agent_id ?? '',
      ).trim()
      if (id && id !== current) return id
    }
  }
  for (const id of plan.blast_radius?.agents ?? []) {
    const trimmed = String(id ?? '').trim()
    if (trimmed && trimmed !== current) return trimmed
  }
  for (const op of plan.operations ?? []) {
    const id = op.agent_id?.trim()
    if (id && id !== current && op.op === 'bind_agent') return id
  }
  return null
}
